import React, { useState, useEffect } from "react";
import { Typography, Grid, Container, Box } from "@mui/material";
import api from "axios";
import { SERVER_BASE_ADDRESS, getBlockchainNodeUrl } from "../globals/const";
import { useLocation } from "react-router-dom";
import ViewProductMap from "../components/ViewProductMap";
import { Pie } from "react-chartjs-2";
import { Chart, ArcElement } from "chart.js";
import { roundDec } from "../globals/utils";
import CustomAppBar from "../components/AppBar";

const styles = {
  icon: {
    marginRight: 2,
  },
  mainContent: {
    // backgroundColor: theme.palette.background.paper,
    padding: 0.25,
    paddingLeft: 0.5,
    paddingRight: 0.5,
    flexGrow: 1,
  },
  row: {
    item: true,
    container: true,
    spacing: 1,
    justify: "center",
  },
  fab: {
    position: "fixed",
    bottom: 2,
    left: 2,
  },
  fabRow: {},
  fundSelectorRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  buttonRow: {
    margin: "10px",
    display: "flex",
    justifyContent: "space-around",
  },

  chartContainer: {
    display: "flex",
    flexDirection: "row",
    height: "40vh",
    width: "15vw",
    margin: "0 auto",
  },

  pieChart: {
    flex: 0.5,
    position: "relative",
  },

  legend: {
    flex: 0.5,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    paddingLeft: "25px",
  },

  legendItem: {
    display: "flex",
    alignItems: "center",
    marginBottom: "30px",
  },

  legendColor: {
    width: "1.5rem",
    height: "1.5rem",
    marginRight: "0.5rem",
  },

  legendLabel: {
    fontSize: "14px",
    fontWeight: "bold",
  },

  typographyContainer: {
    display: "flex",
    justifyContent: "space-between",
  },

  firstTypography: {
    marginRight: "20px",
  },

  secondTypography: {
    marginLeft: "20px",
  },

  qrCode: {
    width: "300px",
    margin: "auto",
  },
};

function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

Chart.register(ArcElement);

export default function ViewProduct() {
  const classes = styles;

  const [isLoading, setIsLoading] = useState(true);
  const [productData, setProductData] = useState({});
  const [pieChartData, setPieChartData] = useState({});

  let urlQuery = useQuery();
  const token_code = urlQuery.get("token_code");

  useEffect(() => {
    const loadProduct = async () => {
      await api
        .get(
          `${SERVER_BASE_ADDRESS}/retail/get-product?product_token=${token_code}`
        )
        .then((res) => res.data)
        .then((data) => {
          setProductData(data);

          const harvest_emissions =
            (data.emission.harvest / data.emission.total) * 100;
          const storage_emissions =
            (data.emission.storage / data.emission.total) * 100;
          const transport_emissions =
            (data.emission.transport / data.emission.total) * 100;
          const processions_emissions =
            (data.emission.processing / data.emission.total) * 100;

          setPieChartData({
            labels: ["Harvest", "Storage", "Transport", "Processing"],
            datasets: [
              {
                data: [
                  harvest_emissions,
                  storage_emissions,
                  transport_emissions,
                  processions_emissions,
                ],
                backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56", "#A020F0"],
                hoverBackgroundColor: [
                  "#FF6384",
                  "#36A2EB",
                  "#FFCE56",
                  "#A020F0",
                ],
              },
            ],
          });
        })
        .catch(() => {});

      setIsLoading(false);
    };

    loadProduct();
  }, [token_code]);

  const pieChartOptions = {
    legend: {
      display: false,
    },
  };

  if (isLoading) {
    return (
      <div>
        <CustomAppBar />
        <Typography
          variant="h3"
          textAlign="center"
          style={{ marginTop: "10px", marginBottom: "10px" }}
        >
          Loading
        </Typography>
      </div>
    );
  }

  return (
    <>
      <CustomAppBar />
      <Container maxWidth="xs">
        <div className={classes.mainContent}>
          <Grid
            container
            // spacing={1}
            justify="center"
            direction="column"
            style={{ marginTop: "10px" }}
          >
            {/* FundSelector */}
            <Grid container item>
              <Grid item xs>
                <Box
                  style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-around",
                    marginTop: "20px",
                  }}
                >
                  <Typography
                    variant="h5"
                    style={{ marginTop: "10px", marginBottom: "10px" }}
                  >
                    Product Trace
                  </Typography>

                  <Typography>
                    Product Name: {productData.product_name}
                  </Typography>
                  <Typography>
                    Serial Number: {productData.serial_number}
                  </Typography>
                  <Typography>
                    Quality at Harvest: {productData.harvest_quality}
                  </Typography>
                  <Typography>
                    Storage Length: {productData.storage_length}
                  </Typography>
                  <Typography>
                    Quality at Manufacturing:{" "}
                    {productData.manufacturing_quality}
                  </Typography>
                  <Typography>
                    Processing Plant: {productData.processing_plant}
                  </Typography>
                  <Typography>
                    Processing Date: {productData.processing_date}
                  </Typography>
                  <Typography>
                    CO2 Emission per kg: {productData.emission.total}
                  </Typography>

                  <div style={styles.firstTypography}>
                    <Typography variant="h6" mt={2}>
                      1. Ingredients
                    </Typography>
                    <ol>
                      {productData.farms ? (
                        <ViewProductMap
                          center={productData.farms[0].polygon[0]}
                        />
                      ) : (
                        <></>
                      )}
                      {productData.ingredients ? (
                        productData.ingredients.map((ingredient, index) => {
                          return (
                            <li key={index}>
                              <a
                                href={
                                  getBlockchainNodeUrl() +
                                  "/get-token?token_code=" +
                                  ingredient.ingredient_token
                                }
                                target="_blank"
                                rel="noreferrer"
                              >
                                <Typography key={index}>
                                  {ingredient.ingredient_token}
                                </Typography>
                              </a>
                            </li>
                          );
                        })
                      ) : (
                        <></>
                      )}
                    </ol>
                  </div>

                  <div style={styles.secondTypography}>
                    <Typography variant="h6" mt={2}>
                      2. Emission Breakdown
                    </Typography>
                    <div style={styles.chartContainer}>
                      <Pie
                        data={pieChartData}
                        options={pieChartOptions}
                        style={styles.pieChart}
                      />
                      <div style={styles.legend}>
                        <div style={styles.legendItem}>
                          <div
                            style={{
                              ...styles.legendColor,
                              backgroundColor: "#FF6384",
                            }}
                          ></div>
                          <div style={styles.legendLabel}>
                            Harvest -{" "}
                            {roundDec(
                              (productData.emission.harvest /
                                productData.emission.total) *
                                100
                            )}
                            %
                          </div>
                        </div>
                        <div style={styles.legendItem}>
                          <div
                            style={{
                              ...styles.legendColor,
                              backgroundColor: "#36A2EB",
                            }}
                          ></div>
                          <div style={styles.legendLabel}>
                            Storage -{" "}
                            {roundDec(
                              (productData.emission.storage /
                                productData.emission.total) *
                                100
                            )}
                            %
                          </div>
                        </div>
                        <div style={styles.legendItem}>
                          <div
                            style={{
                              ...styles.legendColor,
                              backgroundColor: "#FFCE56",
                            }}
                          ></div>
                          <div style={styles.legendLabel}>
                            Transport -{" "}
                            {roundDec(
                              (productData.emission.transport /
                                productData.emission.total) *
                                100
                            )}
                            %
                          </div>
                        </div>
                        <div style={styles.legendItem}>
                          <div
                            style={{
                              ...styles.legendColor,
                              backgroundColor: "#A020F0",
                            }}
                          ></div>
                          <div style={styles.legendLabel}>
                            Processing -{" "}
                            {roundDec(
                              (productData.emission.processing /
                                productData.emission.total) *
                                100
                            )}
                            %
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Typography variant="h6" mt={2}>
                    3. Blockchain History
                  </Typography>
                  {productData.transaction_history.map((transaction, index) => {
                    return (
                      <Typography variant="normal" mt={2} key={index}>
                        {" "}
                        {transaction} {"-->"}{" "}
                      </Typography>
                    );
                  })}
                  <Typography marginTop="10px" textAlign="center">
                    No transportation history
                  </Typography>

                  <img
                    src={`https://chart.googleapis.com/chart?chs=300x300&cht=qr&chl=${window.location.href}&choe=UTF-8`}
                    alt="QR code"
                    style={styles.qrCode}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </div>
      </Container>
    </>
  );
}
